import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Loader2, Clock } from 'lucide-react';
import { Button } from '../../ui/button';
import { STATUTS_RECLAMATION, ORIGINES_RECLAMATION, CANAUX, Badge, Filtres, compterPar } from '../constantes';
import { dateFr } from '../shared';
import { NouvelleReclamationDialog, ReclamationDialog } from './ReclamationDialogs';
import { ActionDialog } from '../amelioration/ActionDialog';

const delaiMoyen = (items) => {
  const traitees = items.filter((r) => r.date_traitement && r.created_at);
  if (!traitees.length) return null;
  return Math.round(traitees.reduce((s, r) => s + (new Date(r.date_traitement) - new Date(r.created_at)) / 86400000, 0) / traitees.length);
};

const CarteReclamation = ({ r, onOpen }) => (
  <button type="button" onClick={() => onOpen(r)} className="w-full rounded-xl border border-slate-200 bg-white p-4 text-left transition-[border-color,box-shadow] hover:border-[#8DC63F] hover:shadow-md" data-testid={`reclamation-${r.reclamation_id}`}>
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-mono text-xs font-semibold text-slate-500">{r.numero}</span>
      <Badge meta={STATUTS_RECLAMATION[r.statut]} testId={`reclamation-${r.reclamation_id}-statut`} />
      <span className="text-xs text-slate-400">reçue le {dateFr(r.date_reception)} · {CANAUX[r.canal]}</span>
    </div>
    <p className="mt-2 text-sm font-semibold text-[#0F172A]">{r.objet}</p>
    <p className="text-xs text-slate-500">{r.auteur_nom} · {ORIGINES_RECLAMATION[r.origine] || r.origine}</p>
    {(r.actions || []).length > 0 && <p className="mt-1 text-xs font-medium text-[#5a8a1f]">Action(s) : {r.actions.map((a) => a.numero).join(', ')}</p>}
  </button>
);

export const RegistreReclamations = ({ onChanged }) => {
  const [items, setItems] = useState(null);
  const [filtre, setFiltre] = useState('');
  const [nouvelle, setNouvelle] = useState(false);
  const [ouverte, setOuverte] = useState(null);
  const [action, setAction] = useState(null);
  const charger = useCallback(() => axios.get('/reclamations').then((r) => { setItems(r.data.items); return r.data.items; }).catch(() => toast.error('Impossible de charger les réclamations')), []);
  useEffect(() => { charger(); }, [charger]);
  const rafraichir = async (maj) => {
    const liste = await charger();
    setOuverte(maj && liste ? liste.find((x) => x.reclamation_id === maj.reclamation_id) || null : null);
    onChanged();
  };
  const creerAction = (r) => { setOuverte(null); setAction({ origine: 'reclamation', origine_ref: r.reclamation_id, origine_libelle: `Réclamation ${r.numero} : ${r.objet}`, constat: r.description, indicateurs: [31, 32] }); };

  if (!items) return <div className="flex justify-center py-12 text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…</div>;
  const delai = delaiMoyen(items);
  const visibles = items.filter((r) => !filtre || r.statut === filtre);
  return (
    <div className="space-y-4" data-testid="registre-reclamations">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Filtres options={STATUTS_RECLAMATION} valeur={filtre} onChange={setFiltre} compteurs={compterPar(items, 'statut')} prefixe="reclamations" />
        <Button onClick={() => setNouvelle(true)} className="bg-[#0F172A] text-white hover:bg-[#1e293b]" data-testid="reclamation-saisir"><Plus className="h-4 w-4 mr-1" /> Saisir une réclamation</Button>
      </div>
      {delai !== null && <p className="flex items-center gap-2 text-sm text-slate-600" data-testid="reclamations-delai"><Clock className="h-4 w-4 text-[#8DC63F]" /> Délai moyen de traitement : <strong>{delai < 1 ? "moins d'un jour" : `${delai} jour${delai > 1 ? 's' : ''}`}</strong></p>}
      {visibles.length === 0
        ? <div className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-500" data-testid="reclamations-vide">Aucune réclamation{filtre ? ' pour ce statut' : ' enregistrée. Les stagiaires, tuteurs et entreprises peuvent en déposer depuis leur espace (menu « Réclamation »)'}.</div>
        : <div className="grid gap-3 lg:grid-cols-2">{visibles.map((r) => <CarteReclamation key={r.reclamation_id} r={r} onOpen={setOuverte} />)}</div>}
      <NouvelleReclamationDialog open={nouvelle} onClose={() => setNouvelle(false)} onSaved={() => { setNouvelle(false); rafraichir(null); }} />
      <ReclamationDialog reclamation={ouverte} onClose={() => setOuverte(null)} onSaved={rafraichir} onCreerAction={creerAction} />
      <ActionDialog action={action} onClose={() => setAction(null)} onSaved={() => { setAction(null); rafraichir(null); }} />
    </div>
  );
};
